import React, { useState } from 'react';
import axios from 'axios';

import classNames from 'classnames';
import queryString from 'query-string';
import hyperid from 'hyperid';
import { createAnnouncer } from '../utils/announcer';
import '../visitor.css';
// import bg from '../static/icons/queuebg.jpg';

const { REACT_APP_MY_LOBBY_BACKEND_URL } = process.env;
const REFRESH_INTERVAL = 4 * 1000;
// fallback sweep in case an enqueue-triggered run was missed; cheap no-op when idle
const SPEAK_SWEEP_INTERVAL = 2 * 1000;
const MAIN_VOICE = 'Zira';
const FALLBACK_VOICE = 'David';
export const VisitorQueue = (props) => {
  const parsedQueryString = queryString.parse(window.location.search);
  const siteId = parseInt(parsedQueryString.siteId, 10);
  const [visitors, setVisitors] = useState(null);
  const [notServing, setNotServing] = useState(null);
  const [site, setSite] = useState();
  const [session, setSession] = useState(null);
  const [time, setTime] = useState(new Date());
  const [soundEnabled, setSoundEnabled] = useState(false);
  const voices = speechSynthesis.getVoices();
  const ref = React.useRef({ voices: [], defaultVoice: null, pollInFlight: false, announcer: null });
  if (!ref.current.announcer) {
    ref.current.announcer = createAnnouncer({
      getVoice: () => ref.current.defaultVoice,
      onSpeechStart: () => setSoundEnabled(true),
    });
  }

  React.useEffect(() => {
    props.hideLoader();
  }, [props]);

  React.useEffect(() => {
    setInterval(() => {
      setTime(new Date());
    }, 1000);
  }, []);

  React.useEffect(() => {
    if (ref.current.voices.length === 0 && voices.length) {
      ref.current.voices = voices;
      findDefaultVoice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voices]);

  React.useEffect(() => {
    let interval;
    if (siteId) {
      getVisitorQueue();
      interval = setInterval(() => {
        getVisitorQueue();
      }, REFRESH_INTERVAL);
    }
    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, session]);

  React.useEffect(() => {
    if (visitors && visitors.length) {
      // announces new "meetingStarted" visitors right away
      ref.current.announcer.enqueue(visitors);
    }
  }, [visitors]);

  React.useEffect(() => {
    let interval = setInterval(() => {
      ref.current.announcer.processQueue();
    }, SPEAK_SWEEP_INTERVAL);

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // iOS Safari blocks speech until the user has interacted with the page,
  // and pauses the engine on long-lived pages. The first tap unlocks it and
  // a resume() heartbeat keeps it from stalling.
  React.useEffect(() => {
    const unlock = () => {
      try {
        speechSynthesis.resume();
        const probe = new SpeechSynthesisUtterance('');
        probe.volume = 0;
        speechSynthesis.speak(probe);
      } catch (e) {
        console.log('Speech unlock failed:', e);
      }
      setSoundEnabled(true);
      document.removeEventListener('pointerdown', unlock);
    };
    document.addEventListener('pointerdown', unlock);
    const heartbeat = setInterval(() => {
      try {
        speechSynthesis.resume();
      } catch (e) {
        /* ignore */
      }
    }, 5000);
    return () => {
      document.removeEventListener('pointerdown', unlock);
      clearInterval(heartbeat);
    };
  }, []);

  const getVisitorQueue = async () => {
    if (ref.current.pollInFlight) return;
    ref.current.pollInFlight = true;
    try {
      let sess = session;
      if (!sess) sess = await initSession();
      const { data } = await axios.get(`${REACT_APP_MY_LOBBY_BACKEND_URL}/api/visitors/visitorQueue/${siteId}`, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: sess.id,
        },
      });
      if (data && data.visitors) {
        const meetingStarted = data.visitors.filter((v) => v.meetingStarted === true);
        const meetingNotStarted = data.visitors.filter((v) => !v.meetingStarted);
        setVisitors(meetingStarted);
        setNotServing(meetingNotStarted);
      } else {
        console.log(`Error:`, data);
      }
    } catch (e) {
      console.log('Queue refresh failed:', e);
    } finally {
      ref.current.pollInFlight = false;
    }
  };

  const getSite = async () => {
    let sess = session;
    if (!sess) sess = await initSession();
    const { data } = await axios.get(`${REACT_APP_MY_LOBBY_BACKEND_URL}/api/sites/${siteId}`, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: sess.id,
      },
    });
    setSite(data.site);
  };

  React.useEffect(() => {
    if (siteId) {
      getSite();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, session]);

  React.useEffect(() => {
    if (notServing?.length > 4) {
      const interval = setInterval(() => {
        const newArray = [...notServing];
        const last = newArray.pop();
        newArray.unshift(last);
        setNotServing(newArray);
      }, 2000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [notServing]);

  const initSession = async () => {
    const id = hyperid().uuid;
    await axios.post(`${REACT_APP_MY_LOBBY_BACKEND_URL}/api/visitor_users`, {
      email: `${id}@my-lobby.com`,
      password: `${id}@my-lobby.com`,
      siteId: siteId,
    });
    const login = await axios.post(`${REACT_APP_MY_LOBBY_BACKEND_URL}/api/visitor_users/login`, {
      email: `${id}@my-lobby.com`,
      password: `${id}@my-lobby.com`,
      siteId: siteId,
    });
    setSession(login.data);
    return login.data;
  };

  const getInitialName = (fullName) => {
    fullName = fullName?.trim();
    let secretName;
    var names = fullName?.split(' ');
    let initials = names ? names[0].substring(0, 1).toUpperCase() : '';
    if (names?.length < 2) {
      return fullName;
    }
    if (names) names.shift();
    secretName = `${initials}. ${names?.join(' ')}`;

    return secretName?.trim();
  };

  function findDefaultVoice() {
    if (!ref.current.voices.length) return null;
    let voice = voices.find((v) => v.name.includes(MAIN_VOICE));
    if (!voice) voice = voices.find((v) => v.name.includes(FALLBACK_VOICE));
    if (!voice) voice = voices.find((v) => v.default);
    if (!voice) voice = voices.find((v) => v.lang.includes('en-US'));
    if (!voice) return null;
    console.log('USING VOICE', voice.name + ' isDefault: ', voice.default);
    ref.current.defaultVoice = voice;
  }

  if (!siteId) {
    return <div>No Site Id provided</div>;
  }
  const siteLogoUrl = session && siteId ? `${REACT_APP_MY_LOBBY_BACKEND_URL}/api/sites/getLogo/${siteId}?access_token=${session.id}` : '';
  return (
    <div
      className="w-full h-screen"
      style={{
        backgroundColor: site?.isSolidColor ? `${site?.backgroundColor}` : site?.isBackGroundImage ? `` : 'black',
        backgroundImage: site?.isBackGroundImage ? `url(${REACT_APP_MY_LOBBY_BACKEND_URL}/api/sites/getBackgroundImage/${site?.id})` : '',
        backgroundSize: 'cover',
      }}
    >
      {!soundEnabled && (
        <div
          style={{
            backgroundColor: '#f5c518',
            color: '#000',
            textAlign: 'center',
            padding: '8px 16px',
            fontSize: 22,
            fontWeight: 600,
          }}
        >
          🔊 Tap the screen once to enable voice announcements
        </div>
      )}
      <header className="header">
        <div className="row">
          <div className="box">
            <span className="time">
              {time
                ? time.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : ''}
            </span>
            <span className="date"> {time ? time.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: '2-digit' }).replaceAll('-', ' ') : 'DATE'}</span>
          </div>
          <div className="box">{siteLogoUrl && <img className="mx-auto h-24 object-center" src={siteLogoUrl} alt={'siteLogo'} />}</div>
          <div className="box">
            {/* <a href="www.fresnohousing.org" className="link">www.fresnohousing.org</a>
            <ul>
              <li><a href="#"><i className="fa fa-instagram" aria-hidden="true"></i></a></li>
              <li><a href="#"><i className="fa fa-facebook" aria-hidden="true"></i></a></li>
              <li><a href="#"><i className="fa fa-twitter" aria-hidden="true"></i></a></li>
            </ul> */}
          </div>
        </div>
      </header>

      <div className="table-responsive" style={{ height: 'auto' }}>
        <div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              borderBottomColor: 'white',
              borderBottomWidth: 1,
            }}
          >
            <span
              style={{
                padding: 15,
                textAlign: 'center',
                borderBottomWidth: 1,
                borderColor: 'solid #000',
                flex: '33.33%',
                color: 'white',
                // backgroundColor: site?.isSolidColor ? `${site?.backgroundColor}` : site?.isBackGroundImage ? `` : 'black',
                fontSize: 64,
                fontWeight: 700,
              }}
            >
              {'Name'}
            </span>
            <span
              style={{
                padding: 15,
                textAlign: 'center',
                borderBottomWidth: 1,
                borderColor: 'solid #000',
                flex: '33.33%',
                color: 'white',
                // backgroundColor: site?.isSolidColor ? `${site?.backgroundColor}` : site?.isBackGroundImage ? `` : 'black',
                fontSize: 64,
                fontWeight: 700,
              }}
            >
              {'Go To'}
            </span>
            <span
              style={{
                padding: 15,
                textAlign: 'center',
                borderBottomWidth: 1,
                borderColor: 'solid #000',
                flex: '33.33%',
                color: 'white',
                // backgroundColor: site?.isSolidColor ? `${site?.backgroundColor}` : site?.isBackGroundImage ? `` : 'black',
                fontSize: 64,
                fontWeight: 700,
              }}
            >
              {'Status'}
            </span>
          </div>
          {!visitors ? (
            <div className="activee">
              <span style={{ display: 'flex', flex: '0.33%', padding: 15 }}></span>
              <span style={{ display: 'flex', flex: '0.33%', padding: 15 }} className="text-center">
                Please wait...
              </span>
              <span style={{ display: 'flex', flex: '0.33%', padding: 15 }}></span>
            </div>
          ) : visitors && visitors.length > 0 ? (
            visitors.map((visitor) => {
              if (visitor?.meetingEnded) return null;
              return (
                <div
                  key={`${visitor?.id}visitor`}
                  style={{ display: 'flex', flexDirection: 'row', borderBottomColor: 'white', borderBottomWidth: 1 }}
                  className={classNames({ activee: visitor?.meetingStarted })}
                >
                  <span
                    style={{
                      color: 'white',
                      flex: '0.33%',
                      padding: 10,
                      // backgroundColor: site?.isSolidColor ? `${site?.backgroundColor}` : site?.isBackGroundImage ? `` : 'black',
                      textAlign: 'center',
                      fontWeight: 400,
                    }}
                  >
                    {getInitialName(visitor?.name)}
                  </span>
                  <span
                    style={{
                      color: 'white',
                      flex: '0.33%',
                      padding: 10,
                      // backgroundColor: site?.isSolidColor ? `${site?.backgroundColor}` : site?.isBackGroundImage ? `` : 'black',
                      textAlign: 'center',
                      fontWeight: 400,
                    }}
                  >
                    {visitor?.meetingDesk ? `${visitor.meetingDesk}` : ''}
                  </span>
                  <span
                    style={{
                      color: 'white',
                      flex: '0.33%',
                      padding: 10,
                      // backgroundColor: site?.isSolidColor ? `${site?.backgroundColor}` : site?.isBackGroundImage ? `` : 'black',
                      textAlign: 'center',
                      fontWeight: 400,
                    }}
                  >
                    {visitor?.meetingStarted ? 'Now Serving' : ''}
                  </span>
                </div>
              );
            })
          ) : null}
          {notServing && notServing.length > 0
            ? notServing.map((v) => {
                return (
                  <div
                    key={`${v?.id}visitor`}
                    style={{ display: 'flex', flexDirection: 'row', borderBottomColor: 'white', borderBottomWidth: 1 }}
                    className={classNames({ activee: v?.meetingStarted })}
                  >
                    <span
                      style={{
                        color: 'white',
                        flex: '0.33%',
                        padding: 10,
                        // backgroundColor: site?.isSolidColor ? `${site?.backgroundColor}` : site?.isBackGroundImage ? `` : 'black',
                        textAlign: 'center',
                        fontWeight: 400,
                      }}
                    >
                      {getInitialName(v?.name)}
                    </span>
                    <span
                      style={{
                        color: 'white',
                        flex: '0.33%',
                        padding: 10,
                        // backgroundColor: site?.isSolidColor ? `${site?.backgroundColor}` : site?.isBackGroundImage ? `` : 'black',
                        textAlign: 'center',
                        fontWeight: 400,
                      }}
                    >
                      {v?.meetingDesk ? `${v.meetingDesk}` : ''}
                    </span>
                    <span
                      style={{
                        color: 'white',
                        flex: '0.33%',
                        padding: 10,
                        // backgroundColor: site?.isSolidColor ? `${site?.backgroundColor}` : site?.isBackGroundImage ? `` : 'black',
                        textAlign: 'center',
                        fontWeight: 400,
                      }}
                    >
                      {v?.meetingStarted ? 'Now Serving' : ''}
                    </span>
                  </div>
                );
              })
            : null}
        </div>
      </div>
    </div>
  );
};
